import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers, withProps, withState } from 'recompose';
import { omit } from 'lodash';
import confirmModal from '../../../../common/confirmModal';

import { buildSteps } from '../FormSteps';
import withBaseUrl from '../../hoc/withBaseUrl';
import withFetchTaxes from '../../../../main/settings/taxes/withFetchTaxes';
import categoriesAndBrandsProvider from '../../../hoc/categoriesAndBrandsProvider';
import poVendorsProvider from '../../../purchaseOrders/vendors/hoc/poVendorsProvider';
import withRoleIdType from '../../../../common/hoc/withRoleIdType';
import withToggle from '../../../../common/hoc/withToggle';

import { getBrandId } from '../../../../auth/selectors';
import { fetchSettings } from '../../Shopify/reducer/actions';
import withMatch from '../../Shopify/withMatch';
import withReduxForm from './withReduxForm';
import { Logger } from '../../../../../modules/infra/observability/logger';

const convertValuesToModel = (values, isBrandUser) => ({
  ...omit(values, ['allEnabled', 'unallocatedInventory', 'locationPrice']),
  price: isBrandUser ? values.locationPrice : values.price,
  inventory: isBrandUser ? values.unallocatedInventory : values.inventory,
  assignments: Object.keys(values.assignments)
    .reduce((acc, ownerId) => [
      ...acc,
      ...values.assignments[ownerId].enabled ? [{
        ...omit(values.assignments[ownerId], ['enabled']),
        ownerId,
      }] : [],
    ], []),
});

const SharedFormContainer = compose(
  connect(
    state => ({
      brandId: getBrandId(state),
      shopifySettings: state.shopify.shopifySettings.settings,
    }),
    { fetchSettings }
  ),
  withReduxForm,
  withBaseUrl,
  withRouter,
  withToggle('submitting', false),
  withRoleIdType,
  withHandlers({
    openListView: props => () => {
      props.router.push(props.baseUrl);
    },
  }),
  categoriesAndBrandsProvider,
  withFetchTaxes,
  withState('activeStep', 'setActiveStep', 0),
  withMatch,
  withProps((props) => {
    const steps = buildSteps(props.isBrandUser);
    const activeStep = steps[props.activeStep];
    const isActiveFormSubmittable = !props.pristine && props.valid;
    const hasNextStep = props.activeStep < steps.length - 1;
    const canGoForward = props.valid && hasNextStep;

    return {
      steps,
      canGoBack: props.activeStep > 0,
      canSubmit: isActiveFormSubmittable && props.activeStep === steps.length - 1,
      isLastStep: props.activeStep === steps.length - 1,
      StepComponent: activeStep.component,
      name: activeStep.name,
      canGoForward,
    };
  }),
  lifecycle({
    componentDidMount() {
      this.props.fetchTaxes();
      this.props.fetchSettings(this.props.brandId);
    },
  }),
  withHandlers({
    submitSKUChanges: props => async (vendorName, remoteIdentifier) => {
      props.submittingOff();
      const newLinkedProducts = remoteIdentifier ?
        { ...props.formValues.linkedProducts, [vendorName]: remoteIdentifier } :
        { ...omit(props.formValues.linkedProducts, vendorName) };

      const linkedProducts = vendorName ? newLinkedProducts : props.formValues.linkedProducts;

      if (vendorName) {
        props.change('linkedProducts', linkedProducts);
      }

      // because redux form values changes asynchronously
      const values = {
        ...props.formValues,
        linkedProducts,
      };


      if (props.canSubmit) {
        try {
          await props.save(convertValuesToModel(values, props.isBrandUser));
        } catch (error) {
          Logger.error('Save product error', {
            error,
            action: 'Save product error',
          });
        } finally {
          props.openListView();
        }

        return;
      }

      if (props.canGoForward) {
        const nextStep = props.activeStep + 1;

        props.setActiveStep(nextStep);
      }
    },
    resetOverrides: props => async (shopId, shopsAmount) => {
      const confirmed = await confirmModal({
        type: 'danger',
        title: 'Restore default',
        message: `Are you sure you want to restore ${
          shopId ? 'this location' : 'all locations'
        } to default values from Product Details? ${
          !shopId && shopsAmount ? `This will affect ${shopsAmount} locations. ` : ''
        }This\xA0action can't be undone.`,
      });

      if (!confirmed) {
        return false;
      }

      const values = {
        ...props.formValues,
      };

      if (shopId) {
        values.assignments[shopId] = {
          ...values.assignments[shopId],
          price: values.price,
          lowInventoryThreshold: values.lowInventoryThreshold,
          highInventoryThreshold: values.highInventoryThreshold,
        };
      } else {
        const assignmentsRolledback = Object.entries(values.assignments)
          .reduce((assignments, [assignmentId, assignment]) => {
          // eslint-disable-next-line no-param-reassign
            assignments[assignmentId] = {
              ...assignment,
              price: values.price,
              lowInventoryThreshold: values.lowInventoryThreshold,
              highInventoryThreshold: values.highInventoryThreshold,
            };

            return assignments;
          }, values.assignments);

        values.assignments = assignmentsRolledback;
      }


      try {
        if (props.isEditing) {
          await props.resetOverridesForShops(convertValuesToModel(values, props.isBrandUser));
        } else {
          props.change('assignments', values.assignments);
        }
      } catch (error) {
        Logger.error('Reset overrides error', {
          error,
          action: 'Reset overrides error',
        });
      }
    },
  }),
  withHandlers({
    trimSku: ({ change, formValues }) => (e) => {
      e.preventDefault();

      if (formValues.sku) {
        change('sku', formValues.sku.trim());
      }
    },
    goBack: props => () => {
      if (props.canGoBack) {
        props.setActiveStep(props.activeStep - 1);
      }
    },
    submitStep: props => async () => {
      props.submittingOn();
    },
  }),
  lifecycle({
    // manually update data in redux form in case inventory was edited
    componentWillUpdate(nextProps) {
      if (!this.props.isEditing) return;

      if (nextProps.product.inventory !== this.props.product.inventory) {
        this.props.change('inventory', nextProps.product.inventory);
      }
    },
  }),
);

const SharedFormWithVendorsContainer = compose(
  poVendorsProvider({ onlyEnabled: true }),
  SharedFormContainer,
);

export const BarberSharedFormContainer = SharedFormContainer;
export const ShopSharedFormContainer = SharedFormWithVendorsContainer;
export const BrandSharedFormContainer = SharedFormWithVendorsContainer;

